// Customer detail view — header + stats + tabs (Library / Orders / Activity)
// + action bar (Attach template / Open as staff / View in Shopify).

import { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, ExternalLink, UserCheck, Send } from 'lucide-react';
import {
  getCustomer,
  getCustomerLibrary,
  getCustomerActivity,
  listTemplates,
  attachToCustomer,
  startImpersonation,
  ApiError,
} from '../lib/api';
import type { Template } from '../lib/types';

type Tab = 'library' | 'activity';

export function CustomerDetailView() {
  const { id } = useParams<{ id: string }>();
  const customerId = id!;
  const qc = useQueryClient();
  const [tab, setTab] = useState<Tab>('library');
  const [showAttach, setShowAttach] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const detailQuery = useQuery({
    queryKey: ['customer', customerId],
    queryFn: () => getCustomer(customerId),
  });
  const libraryQuery = useQuery({
    queryKey: ['customer-library', customerId],
    queryFn: () => getCustomerLibrary(customerId),
    enabled: tab === 'library',
  });
  const activityQuery = useQuery({
    queryKey: ['customer-activity', customerId],
    queryFn: () => getCustomerActivity(customerId),
    enabled: tab === 'activity',
  });

  const impersonateMut = useMutation({
    mutationFn: () => startImpersonation(customerId),
    onSuccess: (resp) => {
      window.open(resp.url, '_blank', 'noopener,noreferrer');
    },
    onError: (err) => setError(err instanceof ApiError ? err.message : (err as Error).message),
  });

  if (detailQuery.isLoading) return <div className="state">Loading…</div>;
  if (detailQuery.isError)
    return <div className="state error">{(detailQuery.error as Error).message}</div>;
  const detail = detailQuery.data!;
  const c = detail.customer;
  const stats = detail.stats;

  return (
    <div className="view">
      <Link to="/customers" className="back-link">
        <ArrowLeft size={14} /> All customers
      </Link>

      <header className="cust-head">
        <div>
          <h1>{[c.first_name, c.last_name].filter(Boolean).join(' ') || c.email}</h1>
          <p className="sub">
            {c.email} · {c.shopify_shop_domain}
            {c.brokerage && <> · {c.brokerage}</>}
          </p>
        </div>
        <div className="cust-head__actions">
          <button className="btn btn--primary" onClick={() => setShowAttach(true)}>
            <Send size={14} /> Attach template
          </button>
          <button
            className="btn"
            onClick={() => impersonateMut.mutate()}
            disabled={impersonateMut.isPending}
          >
            <UserCheck size={14} /> Open as staff
          </button>
          {detail.shopify_admin_url && (
            <a className="btn" href={detail.shopify_admin_url} target="_blank" rel="noreferrer">
              <ExternalLink size={14} /> Shopify
            </a>
          )}
        </div>
      </header>

      {error && <div className="error-banner">{error}</div>}

      <div className="stat-strip">
        <div>
          <span className="stat__num">{stats.total_designs}</span>
          <span className="stat__label">Designs</span>
        </div>
        <div>
          <span className="stat__num">{stats.attached_designs}</span>
          <span className="stat__label">From you</span>
        </div>
        <div>
          <span className="stat__num">{stats.total_orders}</span>
          <span className="stat__label">Orders</span>
        </div>
        <div>
          <span className="stat__num">{stats.total_qty}</span>
          <span className="stat__label">Total qty</span>
        </div>
        <div>
          <span className="stat__num">
            {stats.last_ordered_at ? new Date(stats.last_ordered_at).toLocaleDateString() : '—'}
          </span>
          <span className="stat__label">Last order</span>
        </div>
      </div>

      <div className="tabs">
        <button
          className={tab === 'library' ? 'is-active' : ''}
          onClick={() => setTab('library')}
        >
          Library
        </button>
        <button
          className={tab === 'activity' ? 'is-active' : ''}
          onClick={() => setTab('activity')}
        >
          Activity
        </button>
      </div>

      {tab === 'library' &&
        (libraryQuery.isLoading ? (
          <div className="state">Loading library…</div>
        ) : libraryQuery.isError ? (
          <div className="state error">{(libraryQuery.error as Error).message}</div>
        ) : (libraryQuery.data?.designs.length ?? 0) === 0 ? (
          <div className="state">This customer hasn't built anything yet.</div>
        ) : (
          <div className="grid">
            {libraryQuery.data!.designs.map((d) => (
              <article key={d.id} className="design-card">
                <div className="design-card__thumb">
                  {d.thumbnail_url ? <img src={d.thumbnail_url} alt={d.custom_name ?? ''} /> : null}
                </div>
                <div className="design-card__body">
                  <h4>{d.custom_name ?? d.product_title ?? 'Untitled'}</h4>
                  <span className={`tag tag--${d.source_kind}`}>{d.source_kind}</span>
                  <p className="design-card__meta">
                    {d.total_orders} order{d.total_orders === 1 ? '' : 's'} ·{' '}
                    {new Date(d.updated_at).toLocaleDateString()}
                  </p>
                </div>
              </article>
            ))}
          </div>
        ))}

      {tab === 'activity' &&
        (activityQuery.isLoading ? (
          <div className="state">Loading…</div>
        ) : activityQuery.isError ? (
          <div className="state error">{(activityQuery.error as Error).message}</div>
        ) : (activityQuery.data?.entries.length ?? 0) === 0 ? (
          <div className="state">No admin activity for this customer yet.</div>
        ) : (
          <table className="data-table">
            <thead>
              <tr>
                <th>When</th>
                <th>Admin</th>
                <th>Action</th>
                <th>Details</th>
              </tr>
            </thead>
            <tbody>
              {activityQuery.data!.entries.map((e) => (
                <tr key={e.id}>
                  <td>{new Date(e.created_at).toLocaleString()}</td>
                  <td>{e.admin_email}</td>
                  <td>{e.action}</td>
                  <td>
                    <code style={{ fontSize: 11 }}>{e.payload ? JSON.stringify(e.payload) : '—'}</code>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        ))}

      {showAttach && (
        <AttachModal
          customerId={customerId}
          shopDomain={c.shopify_shop_domain}
          onClose={() => setShowAttach(false)}
          onAttached={() => {
            setShowAttach(false);
            void qc.invalidateQueries({ queryKey: ['customer-library', customerId] });
            void qc.invalidateQueries({ queryKey: ['customer', customerId] });
          }}
        />
      )}
    </div>
  );
}

function AttachModal({
  customerId,
  shopDomain,
  onClose,
  onAttached,
}: {
  customerId: string;
  shopDomain: string;
  onClose: () => void;
  onAttached: () => void;
}) {
  const [templateId, setTemplateId] = useState<string>('');
  const [customName, setCustomName] = useState('');
  const [notify, setNotify] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const templatesQuery = useQuery({
    queryKey: ['templates-attach', shopDomain],
    queryFn: () => listTemplates({ shop: shopDomain }),
  });

  const attachMut = useMutation({
    mutationFn: () =>
      attachToCustomer({
        customer_id: customerId,
        template_id: templateId,
        custom_name: customName || undefined,
        notify,
      }),
    onSuccess: onAttached,
    onError: (err) => setError(err instanceof ApiError ? err.message : (err as Error).message),
  });

  const templates: Template[] = templatesQuery.data?.templates ?? [];

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()}>
        <h3>Attach template</h3>
        {error && <div className="error-banner">{error}</div>}
        <label>
          Template
          <select value={templateId} onChange={(e) => setTemplateId(e.target.value)}>
            <option value="">— pick one —</option>
            {templates.map((t) => (
              <option key={t.id} value={t.id}>
                {t.name}
              </option>
            ))}
          </select>
        </label>
        <label>
          Custom name (optional)
          <input value={customName} onChange={(e) => setCustomName(e.target.value)} />
        </label>
        <label className="checkbox">
          <input type="checkbox" checked={notify} onChange={(e) => setNotify(e.target.checked)} />
          <span>Email the customer</span>
        </label>
        <div className="modal__actions">
          <button onClick={onClose}>Cancel</button>
          <button
            className="btn btn--primary"
            disabled={!templateId || attachMut.isPending}
            onClick={() => attachMut.mutate()}
          >
            {attachMut.isPending ? 'Attaching…' : 'Attach'}
          </button>
        </div>
      </div>
    </div>
  );
}
